import type { FC } from 'react';
import React, { useRef, useState } from 'react';

import { FileText, X, Upload } from 'lucide-react';

import { cn } from '@shared/utils/tailwind.utils';

import { GenericButton } from './buttons/GenericButton';

interface GenericFileUploadProps {
  value: File[];
  onChange: (files: File[]) => void;
  allowedExtensions: string[];
  maxHeightClass?: string;
  multiple?: boolean;
}

export const GenericFileUpload: FC<GenericFileUploadProps> = ({
  value = [],
  multiple = true,
  allowedExtensions,
  onChange,
  maxHeightClass = 'max-h-32',
}) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processAndAppendFiles = (incomingFiles: FileList) => {
    const existingFiles = Array.isArray(value) ? value : [];
    const validNewFiles: File[] = [];

    Array.from(incomingFiles).forEach((file) => {
      const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!allowedExtensions.includes(extension)) return;

      const isDuplicate = existingFiles.some((f) => f.name === file.name);
      if (isDuplicate) return;

      validNewFiles.push(file);
    });

    if (validNewFiles.length === 0) return;

    if (multiple) {
      onChange([...existingFiles, ...validNewFiles]);
    } else {
      const [firstNewFile] = validNewFiles;
      if (firstNewFile) {
        onChange([firstNewFile]);
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processAndAppendFiles(e.dataTransfer.files);
    }
  };

  const formatFileSizeInKB = (bytes: number) => {
    if (bytes === 0) return '0 KB';
    const kb = bytes / 1024;
    return kb.toFixed(1) + ' KB';
  };

  const handleRemoveFile = (indexToRemove: number) => {
    const updatedFiles = value.filter((_, index) => index !== indexToRemove);
    onChange(updatedFiles);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full">
      <section
        aria-label="File upload drop zone"
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        className={cn(
          'block group flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-10 relative select-none transition-all duration-200 ease-out focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary focus-within:ring-inset',
          dragActive
            ? 'border-primary/60 bg-primary/[0.02] ring-2 ring-primary ring-inset shadow-[inset_0_0_12px_rgba(var(--primary),0.05)]'
            : 'border-muted hover:border-primary/30 hover:bg-primary/[0.015]',
        )}
      >
        <div
          className={cn(
            'p-3 rounded-full bg-secondary/50 mb-3 border border-border/40 transition-all duration-300',
            dragActive
              ? 'scale-110 bg-primary/10 border-primary/20 text-primary'
              : 'text-muted-foreground group-hover:text-zinc-300 group-hover:border-primary/60 group-hover:bg-primary/6',
          )}
        >
          <Upload className={cn('h-6 w-6 transition-transform duration-300', dragActive && '-translate-y-0.5')} />
        </div>

        <p className="text-[14px] text-zinc-200 tracking-tight transition-colors duration-200 group-hover:text-foreground">
          <span className="font-semibold text-zinc-100">Drag and drop</span> your file here or click to upload
        </p>

        <div className="flex gap-1.5 mt-3 justify-center">
          {allowedExtensions.map((ext) => (
            <span
              key={ext}
              className={cn(
                'text-[11px] font-mono font-medium px-2 py-0.5 rounded-md border tracking-wider transition-all duration-300',
                dragActive
                  ? 'bg-primary/10 border-primary/30 text-primary shadow-sm'
                  : 'bg-muted/40 text-muted-foreground/80 border-border/60 group-hover:text-zinc-200 group-hover:border-primary/60 group-hover:bg-primary/[0.04]',
              )}
            >
              {ext}
            </span>
          ))}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={allowedExtensions.join(',')}
          className="absolute inset-0 opacity-0 cursor-pointer focus:outline-none"
          onChange={(e) => e.target.files && processAndAppendFiles(e.target.files)}
        />
      </section>

      {value.length > 0 && (
        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between px-1">
            <span className="text-xs font-semibold text-zinc-200 tracking-tight">
              {value.length} file{value.length > 1 ? 's' : ''} staged
            </span>

            {multiple && (
              <GenericButton
                variant="ghost"
                label="Remove all"
                className="h-8 ml-1 text-[12px] text-zinc-300"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange([]);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}
              />
            )}
          </div>

          <ul className={cn('space-y-2 overflow-y-auto pr-1 custom-scrollbar', maxHeightClass)}>
            {value.map((file: File, index: number) => {
              return (
                <li
                  key={file.name + index}
                  className="group/item flex items-center justify-between p-3 rounded-xl bg-muted/20 border border-border/40 hover:bg-muted/30 hover:border-border/80 transition-all duration-200"
                >
                  <div className="flex items-center gap-3 min-w-0 pr-4">
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div className="flex flex-col min-w-0 gap-0.5">
                      <span className="text-sm truncate tracking-tight font-medium text-zinc-200 leading-none">
                        {file.name}
                      </span>
                      <span className="text-[11px] text-muted-foreground/70 font-mono tracking-tight leading-none mt-1">
                        {formatFileSizeInKB(file.size)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      className="p-1.5 rounded-lg border border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary hover:border-border/60 transition-all duration-200 ease-out scale-95 group-hover/item:scale-100 cursor-pointer"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
